import * as React from 'react';
import styled, { css, ThemeContext } from 'styled-components';
import { ColorSet, PaletteGroupKeys } from "--components/theme";
import Color from 'color';
import { Modify } from '../../common/utils';
import { Errors } from '--root/Errors';

const HEIGHT = 18;
const WIDTH = 40;

type StyledCheckboxProps = {
    colorSet: ColorSet
    checked?: boolean
    stateLabels?: { on: React.ReactNode, off: React.ReactNode }
    width: number
    height: number
    border: number
}
type StyledLabelProps = {
    collapse?: boolean
    startLabel?: boolean
    padSides?: number | string
    variant?: "inline"
}
type BaseSwitchProps = Modify<React.InputHTMLAttributes<HTMLInputElement>, {
    color?: ColorSet | PaletteGroupKeys
    onChange?: (e: React.ChangeEvent<HTMLInputElement>, value: boolean) => void
    type?: "checkbox"
    size?: "small" | "medium",
    stateLabels?: { on: React.ReactNode, off: React.ReactNode }
    width?: "default" | "auto" | number
}>
type SwitchProps = BaseSwitchProps & {
    label?: React.ReactNode
    startLabel?: boolean
    collapse?: boolean
    message?: string | Errors.FormFieldError // Error and warning props can be ignored if passing an ErrorMessage object
    error?: boolean
    warning?: boolean
    padSides?: string | number
    variant?: "inline"
}


const StyledCheckbox = styled.div<StyledCheckboxProps>`  
    position: relative;
    width: ${props => props.width}px;
    height: ${HEIGHT}px;
    margin: 0;
    padding: 0;
    border-radius: ${props => props.height / 2}px;
    box-shadow: ${props => props.theme.shadows.inset};
    background: ${props => props.theme.palette.grey.darker};
    border: none;
    outline: none;
    cursor: pointer;
    overflow: hidden;
    transition: background ${props => props.theme.transitions.toggle} linear;

    /* States */
    &.checked {
        background: ${props => props.colorSet.main};
    }
    &:hover:not(:disabled) {
        background: ${props => props.theme.palette.grey.main};
    }
    &.checked:hover:not(:disabled) {
        background: ${props => props.colorSet.lighter};
    }
    &:focus-within:not(:disabled) {
        box-shadow: ${props => props.theme.shadows.inset}, 0 0 4px 0 ${props => Color(props.theme.palette.grey.main).alpha(0.75).toString()};
    }
    &.checked:focus-within:not(:disabled) {
        box-shadow: ${props => props.theme.shadows.inset}, 0 0 4px 0 ${props => Color(props.colorSet.main).alpha(0.75).toString()};
    }
    &:disabled {
        opacity: 0.5;
        cursor: initial;
    }

    /* Movey bit */
    &:after {
        pointer-events: none;
        content: '';
        display: block;
        position: absolute;
        width: ${props => props.height - props.border * 2}px;
        height: ${props => props.height - props.border * 2}px;
        top: ${props => props.border}px;
        left: ${props => props.border}px;
        border-radius: 50%;
        background: ${props => props.theme.palette.surface};
        transition: transform ${props => props.theme.transitions.toggle} ease-out;
        box-shadow: 0 0 2px 2px rgba(0,0,0,0.1);
    }
    &.checked:after {
        transform: translateX(${props => props.width - props.height}px);
    }

    /* Inner Labels */
    &>div {
        height: 100%; 
        width: 100%;
        display: flex;
        align-items: center;
        pointer-events: none;
    }
    &>div>span {
        position: absolute;
        ${props => props.theme.typography.body3}
        margin: 0;
        white-space: nowrap;
    }
    &>div>span:nth-child(1) {
        left: 0;
        padding: 0 4px;
        transform: translateX(-${props => props.width - props.height}px);
        opacity: 0;
        color: ${props => props.colorSet.contrast};
        transition: all ${props => props.theme.transitions.toggle} ease-out;
    }
    &.checked>div>span:nth-child(1) {
        transform: translateX(0);
        opacity: 1;
        transition: all ${props => props.theme.transitions.toggle} ease-out 100ms;
    }
    &>div>span:nth-child(2) {
        right: 0;
        padding: 0 4px;
        transform: translateX(0);
        opacity: 1;
        color: ${props => props.theme.palette.text.secondary};
        transition: all ${props => props.theme.transitions.toggle} ease-out 100ms;

    }
    &.checked>div>span:nth-child(2) {
        transform: translateX(${props => props.width - props.height}px);
        opacity: 0;
        transition: all ${props => props.theme.transitions.toggle} ease-out;
    }

    /* Hidden input */
    &>input {
        pointer-events: auto;
        position: absolute;
        top: 0; left: 0; 
        width: 100%; height: 100%;
        margin: 0; padding: 0;
        appearance: none;
    }
`;

const SwitchContainer = styled.div<StyledLabelProps>`
    display: ${props => props.variant === "inline" ? "inline" : undefined};
    padding: ${props =>
        typeof props.padSides == "number" ?
            props.theme.spacing(0, props.padSides) :
            props.padSides ?
                `0 ${props.padSides}` :
                0
    };
    &>label {
        display: flex;
        justify-content: ${props => props.collapse ? "start" : "space-between"};
        flex-direction: 'row';
        align-items: center;
    }

    ${props => props.collapse ? css`
        &>label>input { 
            margin: ${props.startLabel ? props.theme.spacing(0, 0, 0, 2) : props.theme.spacing(0, 1, 0, 0)}
        }
    ` : null}
`;

const HelperMessage = styled.div<{ state: "disabled" | "error" | "warning" | "default" }>`
    /** Typography */
    ${props => props.theme.typography.body3}
    color: ${props =>
        props.state == "disabled" ?
            props.theme.palette.text.disabled :
            props.state == "error" ?
                props.theme.palette.danger.main :
                props.theme.palette.text.secondary
    };
    margin: 4px 0 0 0;
    padding: 0 8px;
`;

const Label = styled.span<{ size?: "small" | "medium", disabled?: boolean }>`
    ${props => props.size == "small" ? props.theme.typography.body2 : props.theme.typography.body1}
    color:${props => props.disabled ? props.theme.palette.text.disabled : props.theme.palette.text.primary};
    margin: 0;
    padding: 0px ${props => props.theme.spacing(1)};
`;

const BaseSwitch: React.FC<BaseSwitchProps> = (props) => {
    const { color, onChange, size, stateLabels, className, checked, ...inputProps } = props
    const theme = React.useContext(ThemeContext);
    const onLabelRef = React.useRef<HTMLDivElement>(null);
    const offLabelRef = React.useRef<HTMLDivElement>(null);
    const [width, setWidth] = React.useState<number>(typeof props.width == "number" ? props.width : WIDTH);

    let colorSet: ColorSet;
    if (typeof color === "object") colorSet = color;
    else if (!color) colorSet = theme.palette.primary;
    else colorSet = theme.palette[color];

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange && onChange(e, e.target.checked);
    }

    React.useLayoutEffect(() => {
        if (props.width == "auto") {
            let newWidth = Math.max(
                onLabelRef.current && onLabelRef.current.offsetWidth + HEIGHT - 4 || 0,
                offLabelRef.current && offLabelRef.current.offsetWidth + HEIGHT - 4 || 0,
                WIDTH
            );
            setWidth(newWidth);
        }
    }, [stateLabels]);

    return <StyledCheckbox className={`${className} ${checked ? 'checked' : ''}`} width={width} height={HEIGHT} border={HEIGHT / 9} colorSet={colorSet}>
        {stateLabels && <div>
            <span ref={onLabelRef}>{stateLabels.on}</span>
            <span ref={offLabelRef}>{stateLabels.off}</span>
        </div>}
        <input type="checkbox" onChange={handleChange} checked={checked} {...inputProps} />
    </StyledCheckbox>
}

const Switch: React.FC<SwitchProps> = (props) => {
    const { label: labelProp, variant, startLabel, collapse, error, warning, message, size, padSides, className, ...inputProps } = props

    let label = labelProp;
    if (typeof labelProp == "string")
        label = <Label size={size} disabled={props.disabled}>{labelProp}</Label>;

    // Work out what state to render the field in and the message to render
    const errorTest = error || (typeof message == "object" && message.level == "error");
    const warnTest = warning || (typeof message == "object" && message.level == "warning");
    const fieldState = props.disabled ? "disabled" : errorTest ? "error" : warnTest ? "warning" : "default";
    const fieldMessage = typeof message == "object" ? message.message : message;

    return <SwitchContainer variant={variant} collapse={collapse} startLabel={startLabel} padSides={padSides} className={className}>
        <label style={{ position: "relative" }}>
            {startLabel && label}
            <BaseSwitch {...inputProps} />
            {!startLabel && label}
        </label>
        {fieldMessage && <HelperMessage state={fieldState}>{fieldMessage}</HelperMessage>}
    </SwitchContainer>
}

export default Switch
export { BaseSwitch, Switch, BaseSwitchProps, SwitchProps }